<template>
    <div class="report-meter"
         v-if="participantScores && scoreDef">

        <highcharts :options="chartOptions"></highcharts>

        <div class="report-meter__meter-label"
             v-if="label">
            {{ label }}
        </div>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import Colors from '@/providers/GaugeColors'

/**
 * Displays a linear gauge for a score value
 */
export default Vue.extend({
    props: {
        scoreName: String,
        label: String,
        customGaugeColors: {type: Array, default: null} // can also be undefined
    },
    computed: {
        participantScores() {
            return this.$store.state.patientParticipant.latestScoreDefs
        },
        scoreDef() {
            return this.$store.state.patientParticipant.latestScoreDefs[this.scoreName]
        },
        chartOptions() {
            const options = this.getBaseOptions()
            if (this.scoreDef) {
                this.addResponseOptions(options)
            }
            return options
        }
    },
    methods: {
        // Highcharts is not happy if we return an empty config before the score is loaded, so we specify
        // as many properties as we can initially...
        getBaseOptions() {
            return {
                chart: {
                    inverted: true,
                    height: 50,
                    type: 'bullet',
                    backgroundColor: 'transparent'
                },
                legend: {enabled: false},
                plotOptions: {
                    series: {
                        borderWidth: 0,
                        color: 'transparent',
                        targetOptions: {
                            width: '200%',
                            color: '#000',
                            height: 6,
                        }
                    }
                },
                credits: {enabled: false},
                exporting: {enabled: false},
                title: {text: null},
                xAxis: {
                    tickPositions: []
                },
                yAxis: {
                    gridLineWidth: 0,
                    labels: {enabled: false},
                    title: null,
                    startOnTick: false,
                    endOnTick: false,
                    softThreshold: false,
                },
                tooltip: {enabled: false}
            }
        },
        addResponseOptions(options) {
            const val = this.scoreDef.thresholds_are_for_values ? this.scoreDef.score.value : this.scoreDef.score.score
            const thresholds = this.scoreDef.thresholds
            const limits = this.scoreDef.limits
            const higherIsBetter = this.scoreDef.higher_is_better

            // adjust min/max to allow for values that go beyond limits
            let yMin = 0
            let yMax = 0
            if (limits) {
                yMin = +limits[0]
                yMax = +limits[1]
                if (val !== null) {
                    if (val <= yMin) {
                        yMin = val - Math.abs(val / 10)
                    }
                    if (val >= yMax) {
                        yMax = val + Math.abs(val / 10)
                    }
                }
            }
            // console.log(this.scoreDef.score.score_name, thresholds, limits, yMin, yMax, val)

            // calculate plot bands based on different options
            const plotBands = []
            let bandColors = (val !== null ? Colors.GAUGE_COLORS : Colors.GAUGE_DISABLED_COLORS) // see GaugeColors.js
            if (this.customGaugeColors) { // can be null OR undefined
                bandColors = this.customGaugeColors
            }
            if (thresholds) {
                for (var i = 0; i <= thresholds.length; i++) {
                    var band = {}

                    if (i == 0) {
                        band.from = yMin
                        band.to = +thresholds[0]
                        band.color = (higherIsBetter ? bandColors[thresholds.length] : bandColors[0])
                    } else if (i == thresholds.length) {
                        band.from = +thresholds[i - 1]
                        band.to = yMax
                        band.color = (higherIsBetter ? bandColors[0] : bandColors[thresholds.length])
                    } else {
                        band.from = +thresholds[i - 1]
                        band.to = +thresholds[i]
                        band.color = (higherIsBetter ? bandColors[thresholds.length - i] : bandColors[i])
                    }
                    plotBands.push(band)
                }
            }

            options.yAxis.min = yMin
            options.yAxis.max = yMax
            options.yAxis.plotBands = plotBands
            // options.yAxis.reversed = reverseYAxis && !higherIsBetter

            options.series = [{
                data: (val !== null ? [{y: val, target: val}] : []),
                dataLabels: {
                    enabled: false,
                },
            }]
        }
    }
})
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.report-meter {
    width: 100%;
    overflow: hidden;
    margin-left: auto;
    margin-right: auto;
    background-color: transparent;
    //display: inline-block;

    .report-meter__meter-label {
        font-size: .9em;
        color: #666;
        text-align: center;
        margin: -5px 0 10px 0;
    }
}
</style>



